import type { WorkflowNodeData } from '@/types/workflow'

interface BranchByFieldConfigProps {
  data: WorkflowNodeData
  updateField: (field: string, value: any) => void
}

import { Select } from '@/components/ui/Select'
import { Field } from '@/components/ui/Field'
import { inputClass, inputStyle, sectionClass, infoBannerClass, checkboxInputClass } from './_kit'
const FIELD_OPTIONS = [
  // Contact fields
  { value: 'lead_score', label: 'Lead Score', group: 'Contact' },
  { value: 'lead_status', label: 'Lead Status', group: 'Contact' },
  { value: 'seniority_level', label: 'Seniority Level', group: 'Contact' },
  { value: 'company_size', label: 'Company Size', group: 'Contact' },
  { value: 'company_industry', label: 'Company Industry', group: 'Contact' },
  { value: 'tags', label: 'Tags', group: 'Contact' },
  { value: 'job_title', label: 'Job Title', group: 'Contact' },
  { value: 'department', label: 'Department', group: 'Contact' },
  { value: 'country', label: 'Country', group: 'Contact' },
  // People Data Labs enrichment
  { value: 'enrichment_pdl.seniority_level', label: 'PDL Seniority', group: 'People Data Labs' },
  { value: 'enrichment_pdl.job_title', label: 'PDL Job Title', group: 'People Data Labs' },
  { value: 'enrichment_pdl.company_industry', label: 'PDL Industry', group: 'People Data Labs' },
  { value: 'enrichment_pdl.company_size', label: 'PDL Company Size', group: 'People Data Labs' },
  { value: 'enrichment_pdl.company_employee_count', label: 'PDL Employee Count', group: 'People Data Labs' },
  { value: 'enrichment_pdl.country', label: 'PDL Country', group: 'People Data Labs' },
  { value: 'enrichment_pdl.gender', label: 'PDL Gender', group: 'People Data Labs' },
  // Explorium enrichment
  { value: 'enrichment_explorium.job_seniority', label: 'Explorium Seniority', group: 'Explorium' },
  { value: 'enrichment_explorium.job_title', label: 'Explorium Job Title', group: 'Explorium' },
  { value: 'enrichment_explorium.job_department', label: 'Explorium Department', group: 'Explorium' },
  { value: 'enrichment_explorium.company_industry', label: 'Explorium Industry', group: 'Explorium' },
  { value: 'enrichment_explorium.company_employees', label: 'Explorium Employees', group: 'Explorium' },
  { value: 'enrichment_explorium.company_revenue', label: 'Explorium Revenue', group: 'Explorium' },
  { value: 'enrichment_explorium.age_group', label: 'Explorium Age Group', group: 'Explorium' },
  { value: 'enrichment_explorium.email_status', label: 'Explorium Email Status', group: 'Explorium' },
  // Native AI enrichment
  { value: 'enrichment_native.buying_role', label: 'AI Buying Role', group: 'Native AI' },
  { value: 'enrichment_native.authority_score', label: 'AI Authority Score', group: 'Native AI' },
  { value: 'enrichment_native.champion_score', label: 'AI Champion Score', group: 'Native AI' },
  { value: 'enrichment_native.funding_stage', label: 'AI Funding Stage', group: 'Native AI' },
  { value: 'enrichment_native.employee_count', label: 'AI Employee Count', group: 'Native AI' },
  { value: 'enrichment_native.sector_industry', label: 'AI Sector Industry', group: 'Native AI' },
]

const OPERATOR_OPTIONS = [
  { value: 'equals', label: 'Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'less_than', label: 'Less Than' },
  { value: 'is_empty', label: 'Is Empty' },
  { value: 'is_not_empty', label: 'Is Not Empty' },
]

interface Branch {
  id: string
  label: string
  field: string
  operator: string
  value: string
}

const DEFAULT_CONFIG = {
  branches: [{ id: 'branch_1', label: 'Branch 1', field: '', operator: 'equals', value: '' }],
  has_default: true,
}

export default function BranchByFieldConfig({ data, updateField }: BranchByFieldConfigProps) {
  const config = (data.branch_by_field_config || DEFAULT_CONFIG) as {
    branches: Branch[]
    has_default: boolean
  }

  const updateConfig = (updates: Partial<typeof config>) => {
    updateField('branch_by_field_config', { ...config, ...updates })
  }

  const updateBranch = (branchId: string, field: string, value: string) => {
    const updatedBranches = config.branches.map((b) =>
      b.id === branchId ? { ...b, [field]: value } : b
    )
    updateConfig({ branches: updatedBranches })
  }

  const addBranch = () => {
    if (config.branches.length >= 6) return
    const newBranch: Branch = {
      id: `branch_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      label: `Branch ${config.branches.length + 1}`,
      field: '',
      operator: 'equals',
      value: '',
    }
    updateConfig({ branches: [...config.branches, newBranch] })
  }

  const removeBranch = (branchId: string) => {
    updateConfig({ branches: config.branches.filter((b) => b.id !== branchId) })
  }

  return (
    <div className={sectionClass}>
      <Field label="Label">
        {(f) => (
          <input
            {...f}
            type="text"
            className={inputClass} style={inputStyle}
            value={data.label || ''}
            onChange={(e) => updateField('label', e.target.value)}
            placeholder="Branch by Field"
          />
        )}
      </Field>

      <Field label="Branches" as="group" className="space-y-3">
        {config.branches.map((branch) => (
          <div
            key={branch.id}
            className="p-3 bg-muted rounded-xl space-y-2 relative"
          >
            <div className="flex items-center gap-2">
              <input
                type="text"
                className={inputClass} style={inputStyle}
                value={branch.label}
                onChange={(e) => updateBranch(branch.id, 'label', e.target.value)}
                placeholder="Branch label"
              />
              <button
                onClick={() => removeBranch(branch.id)}
                className="p-1.5 text-muted-foreground hover:text-red-500 hover:bg-red-500/15 rounded-lg transition-all duration-150 flex-shrink-0"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <Select
              value={branch.field}
              onChange={(value) => updateBranch(branch.id, 'field', value)}
              groups={[
                { label: '', options: [{ value: '', label: 'Select field...' }] },
                ...Array.from(new Set(FIELD_OPTIONS.map((opt) => opt.group))).map((group) => ({
                  label: group,
                  options: FIELD_OPTIONS.filter((opt) => opt.group === group).map((opt) => ({
                    value: opt.value,
                    label: opt.label,
                  })),
                })),
              ]}
              ariaLabel="Field"
              className="w-full"
            />

            <Select
              value={branch.operator}
              onChange={(value) => updateBranch(branch.id, 'operator', value)}
              options={OPERATOR_OPTIONS}
              ariaLabel="Operator"
              className="w-full"
            />

            {branch.operator !== 'is_empty' && branch.operator !== 'is_not_empty' && (
              <input
                type="text"
                className={inputClass} style={inputStyle}
                value={branch.value}
                onChange={(e) => updateBranch(branch.id, 'value', e.target.value)}
                placeholder="Value"
              />
            )}
          </div>
        ))}

        {config.branches.length < 6 && (
          <button
            onClick={addBranch}
            className="w-full px-3 py-2 border border-dashed border-border/60 rounded-lg text-sm text-muted-foreground hover:border-primary/60 hover:text-primary transition-all duration-150"
          >
            + Add Branch
          </button>
        )}
      </Field>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="has_default"
          checked={config.has_default}
          onChange={(e) => updateConfig({ has_default: e.target.checked })}
          className={checkboxInputClass}
        />
        <label htmlFor="has_default" className="text-sm text-foreground">
          Include default branch for non-matching contacts
        </label>
      </div>

      <div className={infoBannerClass}>
        <p className="text-xs">
          Contacts are routed to the first matching branch, or the default if none match.
        </p>
      </div>
    </div>
  )
}
